import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";
import { AiAvatar, getCategoryProcessingMessage, resetGlobalSpeechState } from "../../components/ui/ai-avatar";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KioskProcessing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationData, badgeImages } = location.state || {};

  const [status, setStatus] = useState("creating");
  const [statusMessage, setStatusMessage] = useState("Creating registration...");
  const [error, setError] = useState(null);
  const [registrationId, setRegistrationId] = useState(null);
  
  // Generate unique submission ID for this attempt - ONCE
  const submissionIdRef = useRef(`submission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Track final outcome type for speech locking
  const [outcomeType, setOutcomeType] = useState(null);  // null = processing, 'error' = failed, 'success' = completed
  
  // Guard against double submissions (React StrictMode runs effects twice)
  const isProcessingRef = useRef(false);
  const hasCompletedRef = useRef(false);

  // Reset global speech state when this page mounts (new submission)
  useEffect(() => {
    resetGlobalSpeechState();
  }, []);

  useEffect(() => {
    if (!registrationData) {
      navigate("/kiosk/category");
      return;
    }

    // GUARD: Prevent double submission
    if (isProcessingRef.current || hasCompletedRef.current) {
      console.log("[KioskProcessing] Skipping duplicate processRegistration call");
      return;
    }
    
    isProcessingRef.current = true;
    processRegistration();
  }, []);

  const processRegistration = async () => {
    let regId = null;
    let registrationResponse = null;
    const startTime = Date.now();
    
    try {
      // Stage 1: Validating (instant feedback)
      setStatus("validating");
      setStatusMessage("Checking your details...");
      
      // Stage 2: Create registration (CRITICAL - this is the main operation)
      setStatus("creating");
      setStatusMessage("Saving your registration...");

      registrationResponse = await axios.post(`${API}/registrations`, registrationData);
      regId = registrationResponse.data.registration_id;
      setRegistrationId(regId);
      
      console.log(`[Timing] Registration created in ${Date.now() - startTime}ms`);
      
      // REGISTRATION SUCCEEDED - from this point on, auxiliary failures should NOT show error to user

    } catch (err) {
      // Registration failed - this is a TRUE failure
      console.error("Registration creation failed:", err);
      hasCompletedRef.current = true;
      setOutcomeType("error");
      setStatus("error");
      setError(err.response?.data?.detail || "An error occurred during registration");
      return;
    }

    // Stage 3: If Blue Badge, process OCR (NON-CRITICAL - runs in background conceptually)
    if (registrationData.category === "disabled_blue_badge" && badgeImages) {
      try {
        setStatus("ocr");
        setStatusMessage("Verifying Blue Badge...");

        const ocrResponse = await axios.post(`${API}/ocr/process`, {
          registration_id: regId,
          front_image_base64: badgeImages.front,
          back_image_base64: badgeImages.back
        });

        console.log(`[Timing] OCR completed in ${Date.now() - startTime}ms`);
        
        if (ocrResponse.data.requires_manual_review) {
          setStatusMessage("Badge queued for verification");
        }
        // No delay after OCR - proceed immediately
      } catch (ocrErr) {
        console.warn("OCR processing failed (non-critical):", ocrErr);
        // Don't delay on failure either
      }
    }

    // Stage 4: Send confirmation email (NON-CRITICAL - fire and forget)
    setStatus("finalizing");
    setStatusMessage("Finalizing...");
    
    // Fire email request but don't wait for response (fire and forget)
    axios.post(`${API}/email/send-confirmation?registration_id=${regId}`).catch(err => {
      console.warn("Email sending failed (non-critical):", err);
    });

    // Brief pause for user to see "Finalizing" (just 300ms for visual feedback)
    await new Promise(resolve => setTimeout(resolve, 300));

    // Stage 5: Complete - navigate to success
    hasCompletedRef.current = true;
    setStatus("complete");
    setStatusMessage("Complete!");
    
    console.log(`[Timing] Total processing time: ${Date.now() - startTime}ms`);
    
    // Brief pause to show "Complete!" before navigation (400ms)
    await new Promise(resolve => setTimeout(resolve, 400));

    // Navigate to confirmation
    navigate("/kiosk/confirmation", {
      state: {
        registration: registrationResponse.data,
        submissionId: submissionIdRef.current,
        outcomeType: "success"
      }
    });
  };
  
  // Compute the message based on outcome
  const getAiMessage = () => {
    if (outcomeType === "error" || status === "error") {
      return "Sorry, something went wrong. Please try again.";
    }
    return getCategoryProcessingMessage(registrationData?.category);
  };

  const getStatusIcon = () => {
    if (status === "error") {
      return <AlertCircle className="w-20 h-20 text-red-500" />;
    }
    if (status === "complete") {
      return <CheckCircle className="w-20 h-20 text-green-500" />;
    }
    return <Loader2 className="w-20 h-20 text-blue-600 animate-spin" />;
  };

  const getProgressWidth = () => {
    switch (status) {
      case "validating": return "10%";
      case "creating": return "40%";
      case "ocr": return "60%";
      case "finalizing": return "85%";
      case "complete": return "100%";
      default: return "0%";
    }
  };

  // Get stage number for display
  const getStageInfo = () => {
    switch (status) {
      case "validating": return { stage: 1, total: 3, text: "Checking details" };
      case "creating": return { stage: 2, total: 3, text: "Saving registration" };
      case "ocr": return { stage: 2, total: 3, text: "Verifying badge" };
      case "finalizing": return { stage: 3, total: 3, text: "Finalizing" };
      case "complete": return { stage: 3, total: 3, text: "Complete!" };
      default: return { stage: 1, total: 3, text: "Processing" };
    }
  };

  const stageInfo = getStageInfo();

  return (
    <div className="kiosk-container flex flex-col min-h-screen p-6 md:p-12" data-testid="kiosk-processing">
      {/* Header */}
      <div className="flex items-center justify-center mb-8">
        <ParkWiseLogo size="lg" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-center animate-fade-in">
          {/* AI Avatar - with global speech lock for error outcomes */}
          <div className="mb-6">
            <AiAvatar 
              message={getAiMessage()} 
              size="medium"
              autoSpeak={true}
              showControls={false}
            />
          </div>

          {/* Status icon */}
          <div className="mb-4">
            {getStatusIcon()}
          </div>

          {/* Status message - more dynamic */}
          <h1 
            className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {status === "error" ? "Registration Failed" : stageInfo.text}
          </h1>

          <p className="text-lg text-slate-600 mb-6">
            {error || statusMessage}
          </p>

          {/* Progress bar - improved */}
          {status !== "error" && (
            <div className="w-full max-w-md mx-auto">
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-300 ease-out"
                  style={{ width: getProgressWidth() }}
                />
              </div>
              <p className="text-sm text-slate-500">
                Step {stageInfo.stage} of {stageInfo.total}
              </p>
            </div>
          )}

          {/* Error retry button */}
          {status === "error" && (
            <button
              onClick={() => navigate("/kiosk")}
              className="kiosk-btn-primary mt-8"
              data-testid="retry-button"
            >
              Start Over
            </button>
          )}
        </div>
      </div>

      {/* Footer message */}
      <div className="text-center text-slate-500">
        <p>Please do not close this screen</p>
      </div>
    </div>
  );
}
